import { TAnimalApplicationTargetUI } from './animalActions.types';
import {
  EAnimalPaymentStatusDB,
  EAnimalPaymentStatusUI,
  EAnimalTagsDB,
  EAnimalTagsUI,
  TAnimalBasicInfo,
  TAnimalBasicInfoUi,
  TAnimalOwnerInfoDB,
  TAnimalOwnerInfoUI,
  TAnimalStatusDetailsDB,
  TAnimalStatusDetailsUI,
  TDataOverviewDB,
  TDataOverviewUI,
} from './animals.types';
import { TSADocPacketGet, TSADocPacketGetUI } from './animalTraining.types';
import { TDocPacketGet, TDocPacketGetUI } from './document.types';
import { TOptionTypeUi, TStatusChangeInfoDB, TStatusChangeInfoUI } from './general.types';
import {
  EAnimalApplicationStatusDB,
  EAnimalApplicationStatusUI,
  EAnimalResidencyStatusDB,
  EAnimalResidencyStatusUI,
  EAnimalResidencyTagDB,
  EAnimalResidencyTagUI,
  EAnimalRestrictionStatusDB,
  EAnimalRestrictionStatusUI,
  EAnimalRestrictionTagDB,
  EAnimalRestrictionTagUI,
  TTenantDeactivationStatusDB,
  TTenantDeactivationStatusUI,
} from './residence.types';
import { TAnimalVetRecord, TAnimalVetRecordUi } from './vetRecord.types';

export enum EMethodDB {
  SIMPLE = 'SIMPLE',
  APPROVE_WITH_APPLICANT_TENANT_RESIDENCY = 'APPROVE_WITH_APPLICANT_TENANT_RESIDENCY',
  APPROVE_WITH_MOVED_IN_TENANT_RESIDENCY = 'APPROVE_WITH_MOVED_IN_TENANT_RESIDENCY',
}

export enum EMethodUI {
  SIMPLE = 'SIMPLE',
  APPROVE_WITH_APPLICANT_TENANT_RESIDENCY = 'APPROVE_WITHOUT_TENANT_RESIDENCY',
  APPROVE_WITH_MOVED_IN_TENANT_RESIDENCY = 'APPROVE_WITH_MOVED_IN_TENANT_RESIDENCY',
}

export type TAppMethodDetailsUI = {
  leaseToDate: string | null;
  moveInDate: string | null;
  property: TAppPropertyUI;
  policy: TAppPolicyUI;
  unit: TAppUnitDB;
};

export type TAppMethodDetailsDB = {
  leaseToDate: string | null;
  moveInDate: string | null;
  policyId: string | null;
  policyName: string | null;
  propertyId: string;
  propertyName: string;
  unitId: string;
  unitNumber: string;
};

export type TWeightProps = {
  unit: string;
  value: string;
};

export type TAppPropertyDB = {
  propertyName: string;
  propertyId: string;
};

export type TAppPropertyUI = TAppPropertyDB;

export type TAppPolicyDB = {
  policyName: string | null;
  policyId: string | null;
};

export type TAppPolicyUI = TAppPolicyDB;

export type TAppUnitDB = {
  unitNumber: string;
  unitId: string;
};

export type TAppUnitUI = TAppUnitDB;

export type TAppResidentDB = {
  residentPhone: string | null;
  residentName: string;
  residentEmail: string;
  residentProfilePicture: string | null;
};

export type TAppResidentUI = TAppResidentDB;

export type TSpeciesProps = {
  predefined: boolean;
  predefinedId: string;
  value: string;
};

export type TAnimalResidenciesDetailsDB = {
  id: string;
  animalName: string;
  animalProfilePicture: string;
  animalType: string;
  animalWeight: TWeightProps;
  animalSpecie: TSpeciesProps;
};

export type TAnimalResidenciesDetailsUI = TAnimalResidenciesDetailsDB;

export type TTenantResidenciesDetailsDB = {
  id: string;
  unitNumber: string;
  propertyName: string;
  animalResidencies: TAnimalResidenciesDetailsDB[];
};

export type TTenantResidenciesDetailsUI = {
  id: string;
  unitNumber: string;
  propertyName: string;
  animalResidencies: TAnimalResidenciesDetailsUI[];
};

export type TTenantResidenciesDB = {
  residencies: TTenantResidenciesDetailsDB[];
};

export type TApplicationDetailDB =
  | {
      residencies?: TTenantResidenciesDetailsDB[];
    }
  | TAppMethodDetailsDB;

export type TApplicationDetailUI =
  | {
      residencies?: TTenantResidenciesDetailsUI[];
    }
  | TAppMethodDetailsUI;

export type TApplicationDetailsUI = {
  resident: TAppResidentDB;
  method: string;
  details: any | TApplicationDetailUI | null;
};

export type TApplicationDetailsDB = {
  residentPhone: string;
  residentName: string;
  residentEmail: string;
  residentProfilePicture: string;
  method: EMethodDB;
  details: { [key in EMethodDB]: TApplicationDetailDB };
};

// Each Action History line changes one half of the split status: an application decision, or the
// residency tag when the animal moves in or out.
export enum EStatusChangeKind {
  APPLICATION = 'APPLICATION',
  RESIDENCY = 'RESIDENCY',
}

// Which move a residency line records; the tags either side cannot tell a move-in from a cancelled move-out.
export enum EResidencyMove {
  MOVED_IN = 'MOVED_IN',
  MOVED_OUT = 'MOVED_OUT',
  MOVE_OUT_CANCELLED = 'MOVE_OUT_CANCELLED',
  MOVE_IN_CANCELLED = 'MOVE_IN_CANCELLED',
}

type TStatusChangeDetailsDB = {
  date: string;
  reason: string;
  by: string | null;
};
export type TStatusChangeHistoryDB = TStatusChangeDetailsDB &
  (
    | { kind: EStatusChangeKind.APPLICATION; from: EAnimalApplicationStatusDB | null; to: EAnimalApplicationStatusDB }
    | {
        kind: EStatusChangeKind.RESIDENCY;
        move: EResidencyMove;
        from: EAnimalResidencyTagDB;
        to: EAnimalResidencyTagDB;
      }
  );

type TStatusChangeDetailsUI = {
  date: string;
  reason: string;
  by: string | null;
};
// A null residency tag means the animal has no tag on that side of the change.
export type TStatusChangeHistoryUI = TStatusChangeDetailsUI &
  (
    | {
        kind: EStatusChangeKind.APPLICATION;
        from: EAnimalApplicationStatusUI | null;
        to: EAnimalApplicationStatusUI | null;
      }
    | {
        kind: EStatusChangeKind.RESIDENCY;
        move: EResidencyMove;
        from: EAnimalResidencyTagUI | null;
        to: EAnimalResidencyTagUI | null;
      }
  );

// PP-10057: why Re-Apply is unavailable, present only when it is. The app explains
// ANIMAL_TYPE_NOT_MANAGED and keeps hiding the action for anything else.
export enum ECanNotReapplyReason {
  ANIMAL_TYPE_NOT_MANAGED = 'ANIMAL_TYPE_NOT_MANAGED',
  OTHER = 'OTHER',
}

export type TAnimalApplicantDetail = {
  animal: TAnimalBasicInfo;
  hasObservableDisability: boolean | null;
  canRetractRejection: boolean;
  canNotReapplyReason: ECanNotReapplyReason | null;
  ownerInfo: TAnimalOwnerInfoDB;
  vetRecords: TAnimalVetRecord[] | null;
  hpDocPackets: TDocPacketGet[] | null;
  residencyStatus: EAnimalResidencyStatusDB;
  statusDetails: TAnimalStatusDetailsDB | null;
  residencyStatusChangeInfo: TStatusChangeInfoDB | null;
  saDocuments: TSADocPacketGet[] | null;
  dataOverview: TDataOverviewDB;
  statusChangeHistory: TStatusChangeHistoryDB[];
  isToPetConverted: boolean;
  policyContent: string | null;
  animalMoveInDate: string;
  tags: EAnimalTagsDB[];
  discoveredTagReason: string | null;
  trainedFor?: boolean;
  isRequiredBecauseOfDisability?: string;
  statusUpdateLocked: boolean;
  isMoveInDatePassed: boolean;
  isScreeningEnabled: boolean;
  toPetConvertedReason: string | null;
  missingNameReason: string | null;
  missingTypeReason: string | null;
  missingMoveInDateReason: string | null;
  toPetConvertedDate: string | null;
  shouldTenantSignAddendum: boolean;
  animalRestrictionStatus: EAnimalRestrictionStatusDB;
  animalRestrictionTags: EAnimalRestrictionTagDB[];
  ticketId: string | null;
  paymentStatus: EAnimalPaymentStatusDB | null;
  deactivationStatusChangeInfo: TTenantDeactivationStatusDB | null;
};

export type TAnimalApplicantDetailUi = {
  animal: TAnimalBasicInfoUi;
  hasObservableDisability: boolean | null;
  canRetractRejection: boolean;
  canNotReapplyReason: ECanNotReapplyReason | null;
  ownerInfo: TAnimalOwnerInfoUI;
  vetRecords: TAnimalVetRecordUi[] | null;
  hpDocPackets: TDocPacketGetUI[] | null;
  residencyStatus: EAnimalResidencyStatusUI;
  splitStatus: TAnimalStatusDetailsUI;
  residencyStatusChangeInfo: TStatusChangeInfoUI | null;
  saDocuments: TSADocPacketGetUI[] | null;
  dataOverview: TDataOverviewUI;
  statusChangeHistory: TStatusChangeHistoryUI[];
  isToPetConverted: boolean;
  policyContent: Blob | null;
  animalMoveInDate: string;
  tags: EAnimalTagsUI[];
  discoveredTagReason: string | null;
  trainedFor?: boolean;
  isRequiredBecauseOfDisability?: string;
  statusUpdateLocked: boolean;
  isMoveInDatePassed: boolean;
  isScreeningEnabled: boolean;
  toPetConvertedReason: string | null;
  missingNameReason: string | null;
  missingTypeReason: string | null;
  missingMoveInDateReason: string | null;
  toPetConvertedDate: string | null;
  shouldTenantSignAddendum: boolean;
  animalRestrictionStatus: EAnimalRestrictionStatusUI;
  animalRestrictionTags: EAnimalRestrictionTagUI[];
  ticketId: string | null;
  paymentStatus: EAnimalPaymentStatusUI | null;
  deactivationStatusChangeInfo: TTenantDeactivationStatusUI | null;
};

export type TApproveApplicationFormProps = {
  propertyId: string;
  unitNumber: string;
  moveInDate: string;
  leaseToDate: string;
};

export type TApproveApplicationFormPropsUI = {
  property: TOptionTypeUi;
  unit: TOptionTypeUi;
  moveInDate: string;
  leaseToDate: string;
};

export type TApproveApplicationUI = TAnimalApplicationTargetUI & {
  residenceId?: string;
  residencyDetails?: TApproveApplicationFormPropsUI;
  conditionalApproveDetails?: {
    dueToDate: string;
    reason: string;
  };
};

export type TApproveApplicationDB = TAnimalApplicationTargetUI & {
  residenceId?: string;
  residencyDetails?: TApproveApplicationFormProps;
  conditionalApproveDetails?: {
    dueDate: string;
    reason: string;
  };
};

export type TApproveApplication = {
  residencyDetails: TApproveApplicationFormProps;
};

export type TReasonProp = {
  [reason in string]: string;
};

export type TVerificationMessageUi = {
  message?: string;
};

export type TVerificationMessageDB = {
  result: TReasonProp[];
};

export type TApprovePassedMoveInApplicationDB = {
  residencyCreated: boolean;
  animalResidencyId: string | null;
};

export type TApprovePassedMoveInApplicationUI = {
  isResidencyCreated: boolean;
  animalResidencyId: string | null;
};

export type TApplicantAnimalScreeningUI = {
  applicationId: string;
  needToScreen: boolean;
};

export type TApplicantAnimalScreeningDB = {
  applicationId: string;
  needToScreen: boolean;
};

export type TAnimalDashboard = {
  esaCount: number;
  saCount: number;
  petCount: number;
  totalCount: number;
};

export type TApplicationActionStatistics = {
  esaStarted: number | null;
  esaAccepted: number | null;
  saStarted: number | null;
  saAccepted: number | null;
  statisticsCollectStartDate: string | null;
};
